import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import MSpinner from "components/Shared/MSpinner.tsx";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionActions from "@mui/material/AccordionActions";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Typography from "@mui/material/Typography";
import { addAppraiserExclusion, removeAppraiserExclusion, getAppraiserExcludedCustomers } from "requests/customerAppraiserExclusions";
import { addInspectorExclusion, removeInspectorExclusion, getInspectorExcludedCustomers } from "requests/customerInspectorExclusions";
import { getCustomersByType } from "requests/customer";
import { LENDER, BOTH } from "typeConstants/customerTypes";
import { dateToString } from "components/Shared/Formatters";

const ExclusionaryList = ({ appraiserNumber, inspectorNumber }) => {
  const [exclusionsList, setExclusionsList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [newComments, setNewComments] = useState("");
  const mounted = useRef(true);
  const customers = useRef([]);

  const getExclusionsList = () => {
    if (appraiserNumber) {
      getAppraiserExcludedCustomers(appraiserNumber, getExcludedCallback, mounted);
    } else if (inspectorNumber) {
      getInspectorExcludedCustomers(inspectorNumber, getExcludedCallback, mounted);
    }
  };

  useEffect(() => {
    getExclusionsList();
  }, [appraiserNumber, inspectorNumber]);

  useEffect(() => {
    const customerType = appraiserNumber ? LENDER : BOTH;
    getCustomersByType(customerType, response => {
      customers.current = response;
    }, mounted);
    return () => {
      mounted.current = false;
    };
  }, []);

  const getExcludedCallback = (response) => {
    if (response.length) {
      const excluded = response.map(o => o.customerExclusionLists)
        .reduce((acc, val) => acc.concat(val), [])
        .map(o => {
          return {
            id: o.id,
            displayLabel: o.removedDate 
              ? `INACTIVE - ${o.customers.displayLabel}` 
              : o.customers.displayLabel,
            createdDate: o.createdDate,
            createdByUserEmail: o.createdByUserEmail,
            comments: o.comments,
            removedDate: o.removedDate,
            removedByUserEmail: o.removedByUserEmail
          };
        });
      setExclusionsList(excluded);
    } else {
      setExclusionsList([]);
    }
    setIsLoading(false);
  };

  const blankExclusion = {
    id: undefined,
    displayLabel: "New Exclusion",
    createdDate: new Date()
  };

  const onAddNew = () => {
    setSelectedCustomer("");
    setNewComments("");
    setExclusionsList([...exclusionsList, blankExclusion]);
  };

  const onSave = () => {
    if (appraiserNumber) {
      addAppraiserExclusion(selectedCustomer, newComments, appraiserNumber, getExclusionsList, mounted);
    } else if (inspectorNumber) {
      addInspectorExclusion(selectedCustomer, newComments, inspectorNumber, getExclusionsList, mounted);
    }
    setSelectedCustomer("");
    setNewComments("");
  };

  const onDelete = (id) => {
    if (!id || typeof id === "object") {
      // User deleted an exclusion that wasn't saved
      setExclusionsList(exclusionsList.filter(e => e.id !== undefined));
      setSelectedCustomer("");
      setNewComments("");
      return;
    }
    if (appraiserNumber) {
      removeAppraiserExclusion(id, getExclusionsList, mounted);
    } else if (inspectorNumber) {
      removeInspectorExclusion(id, getExclusionsList, mounted);
    }
  };

  if (isLoading) return <MSpinner />;

  return (
    <div className="md-grid">
      <div className="md-cell md-cell--12 md-cell--right">
        {exclusionsList && exclusionsList.map((exclusion) => {
          return (
            <Accordion className="md-cell md-cell--12 md-cell--right"
              key={exclusion.id ?? 0}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>{exclusion.displayLabel}</Typography>
              </AccordionSummary>
              <AccordionDetails className="md-grid">
                {exclusion.id &&
                  <div className="full-width">
                    <p>Added {dateToString(exclusion.createdDate)} by {exclusion.createdByUserEmail}</p>
                    <br />
                    {exclusion.removedDate &&
                      <><p>Removed {dateToString(exclusion.removedDate)} by {exclusion.removedByUserEmail}</p>
                      <br /></>
                    }
                  </div>
                }
                {!exclusion.id &&
                  <>
                    <TextField
                      select
                      variant="standard"
                      id="exclusion-customer-select"
                      label="Customer"
                      fullWidth
                      onChange={(e) => setSelectedCustomer(e.target.value)}
                      value={selectedCustomer}
                    >
                      {customers.current.map(x => (
                        <MenuItem key={x.number} value={x.number}>{x.displayLabel}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      variant="standard"
                      id="exclusion-comments-text-field"
                      label="Comments"
                      fullWidth
                      onChange={(e) => setNewComments(e.target.value)}
                      value={newComments}
                    />
                  </>}
                {exclusion.id &&
                  <>
                    {exclusion.comments &&
                      <div className="full-width">
                        <p>{exclusion.comments}</p>
                      </div>}
                    {!exclusion.removedDate && <div className="md-cell md-cell--12 offset-md--4">
                      <i className="md-icon material-icons" onClick={() => onDelete(exclusion.id)}>delete</i>
                    </div>}
                  </>}
              </AccordionDetails>
              <AccordionActions>
                <Button variant="text" disabled={exclusion.id ? true : false} onClick={onSave}>Save</Button>
                <Button variant="text" onClick={exclusion.id ? () => { } : onDelete}>Cancel</Button>
              </AccordionActions>
            </Accordion>);
        })}
      </div>
      <Button className="md-cell md-cell--12 md-cell--right"
        variant="text"
        color="secondary"
        startIcon={<span className="material-icons">add</span>}
        onClick={onAddNew}>
        Add Exclusion
        </Button>
    </div>
  );
};

ExclusionaryList.propTypes = {
  appraiserNumber: PropTypes.number,  // If appraiser number is passed, this will call Customer Appraiser Exclusions endpoints
  inspectorNumber: PropTypes.number,  // If inspector number is passed, this will call Customer Inspector Exclusions endpoints
};

export default ExclusionaryList;